<template>
  <div class="flex-1 relative h-full">
    <div v-if="!shouldLoadMap" class="absolute inset-0 bg-gray-50 flex items-center justify-center">
      <div class="flex flex-col items-center gap-2 opacity-30">
        <MapPin :size="48" class="text-gray-400" />
        <span class="text-sm font-medium text-gray-400">{{ t('searchResults.mapPlaceholder') }}</span>
      </div>
    </div>
    <LMap
      v-if="shouldLoadMap"
      ref="map"
      class="map absolute inset-0 ring-1 ring-gray-300 sm:ring-0"
      :zoom="mapDefaults.zoom"
      :center="[mapDefaults.center.lat, mapDefaults.center.lng]"
      :use-global-leaflet="false"
      :options="{ zoomControl: false }"
      @ready="onReady"
      @moveend="onMoveEnd"
    >
      <LControlZoom position="bottomright" zoom-in-text="+" zoom-out-text="−" />
      <LTileLayer
        v-if="baseLayerTier === 'gsi'"
        :url="gsiBaseTileLayer.url"
        :attribution="gsiBaseTileLayer.attribution"
        :min-zoom="gsiBaseTileLayer.minZoom"
        :max-zoom="gsiBaseTileLayer.maxZoom"
        layer-type="base"
      />
      <LTileLayer
        v-else-if="baseLayerTier === 'osm'"
        :url="osmTileUrl"
        :attribution="osmBaseTileLayer.attribution"
        :min-zoom="osmBaseTileLayer.minZoom"
        :max-zoom="osmBaseTileLayer.maxZoom"
        layer-type="base"
        @tileerror="onOsmTileError"
      />
      <LTileLayer
        v-for="id in selectedHazardMapTypes"
        :key="`hazard-${id}`"
        :url="hazardMapTypes[id].urlPattern"
        :opacity="hazardMapTypes[id].opacity ?? 0.5"
        :min-zoom="hazardMapTypes[id].minZoom"
        :max-zoom="hazardMapTypes[id].maxZoom"
        pane="overlayPane"
      />

      <slot name="mobile-toggle" :is-mobile="isMobile"></slot>

      <!-- Hazard Map Controls -->
      <div class="absolute top-4 right-4 z-[1000]">
        <div class="relative">
          <button
            class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer
            transition-colors shadow-md border border-gray-300"
            :class="showHazardMapPanel ? 'bg-brand hover:bg-brand/90 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'"
            @click="showHazardMapPanel = !showHazardMapPanel"
          >
            <Layers :size="16" />
            <span>{{ t('searchResults.hazardMaps') }}</span>
          </button>
          <div
            v-if="showHazardMapPanel"
            class="absolute top-full mt-2 right-0 bg-white shadow-lg rounded-md p-4 z-10 w-max
            max-h-70 overflow-y-auto max-w-90"
            @wheel.stop
          >
            <div class="flex items-center justify-between gap-4 mb-3">
              <h4 class="font-semibold text-gray-700">{{ t('searchResults.selectHazardLayers') }}</h4>
              <button type="button" @click="showHazardMapPanel = false" class="text-gray-400 hover:text-gray-600">
                <XMarkIcon class="size-5" />
              </button>
            </div>
            <div class="space-y-1">
              <label
                v-for="(map, id) in hazardMapTypes"
                :key="id"
                class="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  v-model="selectedHazardMapTypes"
                  type="checkbox"
                  :value="id"
                />
                <span>{{ t(`hazardMap.${id}.name`) }}</span>
                <span v-if="map.sub" class="text-xs text-gray-400 ml-1">{{ t(`hazardMap.${id}.sub`) }}</span>
              </label>
            </div>
            <p class="text-xs text-gray-400 mt-3 whitespace-pre-wrap">{{ t('property.copyright.disaportal') }}</p>
          </div>
        </div>
      </div>

      <!-- Remove Boundary Control -->
      <div v-if="hasBoundary" v-show="!mapLoading" class="absolute top-0 left-0 z-[1000]">
        <button
          type="button" class="m-4 text-base cursor-pointer gap-x-0.5 rounded-lg
          bg-white px-3 py-2 hover:bg-gray-50 text-gray-700 shadow-md flex flex-row items-center gap-2 border border-gray-300"
          @click="$emit('remove-boundary')"
        >
          <span>{{ t('searchResults.removeBoundary') }}</span>
          <XMarkIcon class="size-5" />
        </button>
      </div>

      <template v-if="blankIcon">
        <!-- Shapes -->
        <LPolygon
          v-for="(path, i) of mapPolygons"
          :key="'poly-' + i"
          :lat-lngs="path"
          v-bind="mapPolygonStyle"
        />
        <LCircle
          v-for="(circle, i) of mapCircles"
          :key="'circle-' + i"
          v-bind="circle"
        />
        <LPolyline
          v-for="(path, i) of mapLineStrings"
          :key="'line-' + i"
          :lat-lngs="path"
          v-bind="mapPolylineStyle"
        />

        <!-- Markers -->
        <LMarker
          v-for="pin of pins"
          :key="pin.id"
          :lat-lng="[pin.geometry.coordinates[1], pin.geometry.coordinates[0]]"
          :icon="blankIcon"
        >
          <LTooltip :options="markerTooltipOptions">
            <div
              :class="[
                'px-3 py-1.5 rounded-lg shadow-lg transition-all',
                activeMarkerId === pin.id
                  ? 'bg-brand text-white scale-110 z-10'
                  : 'bg-white text-gray-900 hover:bg-brand hover:text-white',
              ]"
              @click.stop="$emit('marker-click', pin)"
            >
              <div class="text-xs font-bold whitespace-nowrap">
                {{ pin.price?.min ? formatPrice(pin.price.min) : formatPrice(pin.rent.min) }}
                <template v-if="pin.price?.min ? pin.price.min < pin.price.max : pin.rent.min < pin.rent.max">
                  - {{ pin.price?.max ? formatPrice(pin.price.max) : formatPrice(pin.rent.max) }}
                </template>
              </div>
            </div>
            <MapPin
              :size="20"
              :class="[
                'absolute left-1/2 -translate-x-1/2 -bottom-4 transition-colors',
                activeMarkerId === pin.id ? 'text-brand' : 'text-gray-600',
              ]"
              fill="currentColor"
            />
          </LTooltip>
        </LMarker>

        <!-- Active Card Popup -->
        <LMarker
          v-if="activeCard"
          :lat-lng="[
            (activeCluster || activeCard).geometry.coordinates[1],
            (activeCluster || activeCard).geometry.coordinates[0]
          ]"
          :icon="blankIcon"
        >
          <LTooltip :options="activeCardTooltipOptions">
            <PropertyCard
              v-bind="activeCard"
              :closable="true"
              :agents="activeCardAgents"
              :photo-priority="photoPriority"
              class="absolute top-4 left-1/2 -translate-x-1/2
              bg-white rounded whitespace-nowrap z-10 shadow min-w-[90vw] md:min-w-[300px] max-w-full"
              @close="$emit('close-card')"
            />
          </LTooltip>
        </LMarker>
      </template>

      <!-- Legend -->
      <div
        v-if="selectedHazardMapTypes.length"
        class="legend absolute left-2.5 bottom-7 z-[1000]"
      >
        <img
          class="!max-w-30 md:!max-w-50 bg-white"
          :src="hazardMapTypes[selectedHazardMapTypes[selectedHazardMapTypes.length - 1]].legendUrl"
        />
      </div>
    </LMap>
  </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css';
import { ref, watch, computed, useTemplateRef, onMounted } from 'vue';
import type L from 'leaflet';
import { Layers, MapPin } from 'lucide-vue-next';
import { XMarkIcon } from '@heroicons/vue/20/solid'
import { LMap, LTileLayer, LControlZoom, LMarker, LTooltip, LPolygon, LPolyline, LCircle } from '@vue-leaflet/vue-leaflet'
import { usePageContext } from 'vike-vue/usePageContext';
import { useI18n } from 'vue-i18n';
import { useMap, type MapParams } from '../composables/map';
import { getInitialCenter, gsiBaseTileLayer, osmBaseTileLayer } from '../utils/map';
import PropertyCard from './PropertyCard.vue';
import { types as hazardMapTypes } from '@repo/modules/hazardMap';
import { centroid } from '@turf/centroid'
import * as turf from '@turf/helpers'
import { area } from '@turf/area'
import { hash } from '../utils/object';
import { useMediaQuery } from '../composables/useMediaQuery';
import { useCurrency } from '../composables/units';
import { isBot } from '../utils/bot';
import { type Pin } from '@repo/ui';
import type { Deal, Property, Page, LinkedPage, Agent, AgentWithProfiles, UserProfile, OrganizationProfile, Place, Station, Region, Line } from '@repo/types';

const props = defineProps<{
  place: Place | Line | Page | null;
  pins: Pin[];
  activeMarkerId: string | null;
  activeCard: Property | null;
  activeCardAgents: AgentWithProfiles[];
  photoPriority?: string;
  mode?: string;
}>();

const emit = defineEmits(['remove-boundary', 'marker-click', 'close-card']);

const { t } = useI18n();
const pageContext = usePageContext();

const userInteracted = ref(true); // don't require interaction for now
const shouldLoadMap = computed(() => {
  return pageContext.isClientSide && !isBot() && userInteracted.value;
});

onMounted(() => {
  if (!pageContext.isClientSide || isBot()) return;
  const handler = () => {
    userInteracted.value = true;
    window.removeEventListener('mousemove', handler);
    window.removeEventListener('touchstart', handler);
    window.removeEventListener('scroll', handler);
    window.removeEventListener('keydown', handler);
    window.removeEventListener('wheel', handler);
    window.removeEventListener('mousedown', handler);
  };
  window.addEventListener('mousemove', handler, { passive: true, once: true });
  window.addEventListener('touchstart', handler, { passive: true, once: true });
  window.addEventListener('scroll', handler, { passive: true, once: true });
  window.addEventListener('keydown', handler, { passive: true, once: true });
  window.addEventListener('wheel', handler, { passive: true, once: true });
  window.addEventListener('mousedown', handler, { passive: true, once: true });
});

const mapRef = useTemplateRef('map');
const { mapDefaults, mapParams: internalMapParams, mapLoading, baseLayerTier, osmTileUrl, onOsmTileError, onReady: onMapReady, updateBounds } = useMap({
  zoom: props.place?.level === 'station' ? 16 : props.mode === 'buy' ? 13 : 15,
  center: getInitialCenter(props.place)
});

function onReady(leafletMap: L.Map) {
  onMapReady(leafletMap);
}

function onMoveEnd() {
  const leafletMap = mapRef.value?.leafletObject;
  if (leafletMap) updateBounds(leafletMap);
}

const mapParams = defineModel<MapParams>('mapParams');

watch(internalMapParams, (val) => {
  mapParams.value = val;
}, { immediate: true });

const showHazardMapPanel = ref(false);
const selectedHazardMapTypes = ref<string[]>([]);

const isMobile = useMediaQuery('(max-width: 1023px)');
const { formatShort: formatPrice } = useCurrency(pageContext.data.currencies);

const blankIcon = ref<L.DivIcon | null>(null);
onMounted(async () => {
  const L = await import('leaflet');
  blankIcon.value = L.divIcon({ className: 'leaflet-blank-icon', iconSize: [0, 0] });
});

const markerTooltipOptions = {
  permanent: true,
  direction: 'center' as const,
  interactive: true,
  className: 'leaflet-marker-tooltip',
};

const activeCardTooltipOptions = {
  ...markerTooltipOptions,
  className: 'leaflet-marker-tooltip leaflet-marker-tooltip-active',
};

defineExpose({ map: mapRef });

const activeCluster = computed(() => props.activeMarkerId ? props.pins.find(p => p.id === props.activeMarkerId) : null);

// Geometry Logic
const point = computed(() => {
  if (props.place?.level !== 'station' || !('geometry' in props.place)) return null;
  const coords = (props.place as Station).geometry.coordinates.flat();
  const sum = coords.reduce((a: { lat: number; lng: number }, b: [number, number]) => ({ lat: a.lat + b[1], lng: a.lng + b[0] }), { lat: 0, lng: 0 });
  return { lat: sum.lat / coords.length, lng: sum.lng / coords.length };
});

const mapPolygonStyle = { color: '#525252', opacity: 0.9, weight: 3, fillColor: '#525252', fillOpacity: 0.03 };
const mapPolylineStyle = { color: '#1e3a5f', opacity: .5, weight: 5, fillColor: '#1e3a5f', fillOpacity: 0.03 };

const mapCircles = computed(() => point.value ? [{
  latLng: point.value,
  radius: 1600,
  color: '#525252',
  opacity: 0.9,
  weight: 1,
  fillColor: '#525252',
  fillOpacity: 0.05
}] : []);

const lineStrings = computed(() => (props.place && 'geometry' in props.place && props.place.geometry.type.includes('LineString') && props.place.level !== 'station') ? (props.place.geometry.coordinates as [number, number][][]) : []);
const mapLineStrings = computed(() => lineStrings.value.map((line: [number, number][]) => line.map(([lng, lat]: [number, number]) => ({ lat, lng }))));

const polygons = computed(() => {
  if (!props.place || !('geometry' in props.place) || !props.place.geometry.type.includes('Polygon')) return [];
  const geom = props.place.geometry as Region['geometry'];
  return geom.type === 'MultiPolygon' ? (geom.coordinates as [number, number][][][]).map((p: [number, number][][]) => p[0]) : (geom.coordinates as [number, number][][]);
});
const mapPolygons = computed(() => polygons.value.map((path: [number, number][]) => path.map(([lng, lat]: [number, number]) => ({ lat, lng }))));
const mainPolygon = computed(() => [...polygons.value].sort((a, b) => area(turf.polygon([b])) - area(turf.polygon([a]))).shift());

const hasBoundary = computed(() => !!(mainPolygon.value || lineStrings.value.length || point.value));

// Bounds fitting
let oncePerPlace: { lat: number; lng: number } | [number, number][][] | null;
watch([mapLoading, point, mainPolygon, lineStrings], async ([loading, pt, poly, lines]) => {
  if (loading || !mapRef.value?.leafletObject) return;
  const leafletMap = mapRef.value.leafletObject;
  if (pt && await hash(oncePerPlace) !== await hash(pt)) {
    oncePerPlace = pt;
    leafletMap.setView([pt.lat, pt.lng], 14);
  } else if ((poly || lines.length) && await hash(oncePerPlace) !== await hash(poly || lines)) {
    oncePerPlace = poly || lines;
    const latLngs: [number, number][] = poly
      ? poly.map(([lng, lat]: [number, number]) => [lat, lng])
      : lines.flat().map(([lng, lat]: [number, number]) => [lat, lng]);
    if (latLngs.length) leafletMap.fitBounds(latLngs, { padding: poly ? [0, 0] : [40, 40] });
  }
});
</script>

<style scoped>
:global(.leaflet-marker-tooltip) {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
}
:global(.leaflet-marker-tooltip::before) {
  display: none;
}
:global(.leaflet-marker-tooltip-active) {
  z-index: 1000 !important;
}
</style>
